import { Injectable } from "@angular/core";
import { ConfigServiceValue } from "@fabric-types/svc-config";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import { ActionWithPayload } from "@obg/ngrx-utils";
import { AppError, MessageService } from "@obg/ngx.core";
import { DocumentRef } from "@obg/ngx.global.evo";
import { merge, Observable, of } from "rxjs";
import { catchError, concatMap, map, switchMap, withLatestFrom } from "rxjs/operators";

import { CallingCodeAction } from "../calling-code/calling-code.action";
import { CallingCodeConfigService } from "../calling-code/calling-code.config";
import { CommonState } from "../state";

import { COUNTRY_ACTION_TYPE, CountryAction } from "./country.action";
import { COUNTRY_MESSAGE_CONTEXT } from "./country.const";
import { CountryService } from "./country.service";

@Injectable()
/**
 * @deprecated [fabric-deprecated] - use mfe svc-content CountryService instead
 */
export class CountryEffect {
	/**
	 * @deprecated [fabric-deprecated] - use "document.obgEventBus.retrieve(ContentValue.getTransformedCountries)" instead
	 */
	getAll$ = createEffect(() =>
		this.actions$.pipe(
			ofType(COUNTRY_ACTION_TYPE.getAll),
			switchMap(() =>
				this.countryService.getCountries().pipe(
					map((response) => {
						if (this.config.getIsSingleMarketCallingCodesEnabled()) {
							this.store.dispatch(this.callingCodeAction.getAll());
						}
						return this.countryAction.getAllSuccess(response);
					}),
					catchError((error: AppError) => of(this.countryAction.getAllFail(error)))
				)
			)
		)
	);
	/**
	 * @deprecated [fabric-deprecated] - use "document.obgEventBus.retrieve(ContentValue.getBirthCountries)" instead
	 */
	getAllBirthCountries$ = createEffect(() =>
		this.actions$.pipe(
			ofType(COUNTRY_ACTION_TYPE.getAllBirthCountries),
			switchMap(() =>
				this.countryService.getBirthCountries().pipe(
					map((response) => {
						if (this.config.getIsSingleMarketCallingCodesEnabled()) {
							this.store.dispatch(this.callingCodeAction.getAll());
						}
						return this.countryAction.getAllBirthCountriesSuccess(response);
					}),
					catchError((error: AppError) => of(this.countryAction.getAllBirthCountriesFail(error)))
				)
			)
		)
	);
	/**
	 * @deprecated [fabric-deprecated] - use "document.obgEventBus.retrieve(ContentValue.getCountryStates)" instead
	 */
	getAllStates$ = createEffect(() =>
		this.actions$.pipe(
			ofType<ActionWithPayload<string>>(COUNTRY_ACTION_TYPE.getAllStates),
			concatMap((action) =>
				of(action).pipe(
					withLatestFrom(
						new Observable(this.documentRef.obgEventBus.retrieve(ConfigServiceValue.CountryCode))
					)
				)
			),
			switchMap(([countrySelection, countryCode]) =>
				this.countryService.getCountryStates(countrySelection.payload, countryCode).pipe(
					map((response) => this.countryAction.getAllStatesSuccess(response)),
					catchError((error: AppError) => of(this.countryAction.getAllStatesFail(error)))
				)
			)
		)
	);
	/**
	 * @deprecated [fabric-deprecated] - use "document.obgEventBus.retrieve(ContentValue.getCountryStatesFromContent)" instead
	 */
	getAllStatesFromContent$ = createEffect(() =>
		this.actions$.pipe(
			ofType<ActionWithPayload<string>>(COUNTRY_ACTION_TYPE.getAllStatesFromContent),
			switchMap((jurisdiction) =>
				this.countryService.getCountryStatesFromContent(jurisdiction.payload).pipe(
					map((states) => this.countryAction.getAllStatesFromContentSuccess({ states })),
					catchError((error: AppError) => of(this.countryAction.getAllStatesFromContentFail(error)))
				)
			)
		)
	);
	/**
	 * @deprecated [fabric-deprecated] - use "document.obgEventBus.retrieve(ContentValue.getAdministrativeAreasByCountryCode)" instead
	 */
	getAdministrativeAreas$ = createEffect(() =>
		this.actions$.pipe(
			ofType<ActionWithPayload<string>>(COUNTRY_ACTION_TYPE.getAdministrativeAreas),
			map((action) => action.payload),
			switchMap((countryCode) =>
				this.countryService.getAdministrativeAreas(countryCode).pipe(
					map((response) => this.countryAction.getAdministrativeAreasSuccess(response, countryCode)),
					catchError((error) => of(this.countryAction.getAdministrativeAreasFail(error)))
				)
			)
		)
	);

	initMessages$ = createEffect(() =>
		merge(
			this.messageService.mapErrorByContext$({
				[COUNTRY_ACTION_TYPE.getAllFail]: COUNTRY_MESSAGE_CONTEXT.getAll,
			}),
			this.messageService.mapClearByContext$({
				[COUNTRY_ACTION_TYPE.getAll]: COUNTRY_MESSAGE_CONTEXT.getAll,
			})
		)
	);

	constructor(
		private actions$: Actions,
		private countryAction: CountryAction,
		private countryService: CountryService,
		private messageService: MessageService,
		private callingCodeAction: CallingCodeAction,
		private store: Store<CommonState>,
		private config: CallingCodeConfigService,
		private documentRef: DocumentRef
	) {}
}
